import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { keepPreviousData, useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { LogIn, Activity, Download, Check, X as XIcon, Globe, Monitor } from 'lucide-react';
import apiClient from '../../../api/client.ts';
import Toast, { useToast } from '../../Toast';
import AuditFilterBar, { rangeForPreset, type AuditRange } from './AuditFilterBar';
import AuditKpiCard from './AuditKpiCard';
import LoginActivityTable, { type LoginRow } from './LoginActivityTable';
import LoginDetailPanel from './LoginDetailPanel';
import ActivityTimeline, { type ActivityRow } from './ActivityTimeline';
import type { ActivityType, LoginResult } from './auditConstants';
import './audit.css';

type View = 'logins' | 'activity';

interface LoginsResponse {
  rows: LoginRow[];
  total: number;
  kpis: {
    successes24h: number; successUsers24h: number; failed24h: number;
    failedTopIpCount: number; failedTopIpBlocked: boolean;
    uniqueIps24h: number; newIps24h: number; flaggedIps24h: number;
    activeSessions: number; lockedAccounts: number;
  };
  resultCounts: Partial<Record<LoginResult, number>>;
}
interface ActivityResponse {
  rows: ActivityRow[];
  nextCursor: number | null;
  total: number;
  typeCounts: Partial<Record<ActivityType, number>>;
}

const PAGE_SIZE = 50;
// Switching views re-enables a query; within this window it reuses the cache instead of refetching.
const AUDIT_STALE_MS = 30_000;

function loadPersisted(): { view: View; range: AuditRange } {
  try {
    const view = (sessionStorage.getItem('auditlog_view') as View) ?? 'logins';
    const storedRange = sessionStorage.getItem('auditlog_range');
    const range: AuditRange = storedRange ? JSON.parse(storedRange) : rangeForPreset('7d');
    return { view: view === 'activity' ? 'activity' : 'logins', range };
  } catch {
    return { view: 'logins', range: rangeForPreset('7d') };
  }
}

export default function AuditLog() {
  const { toast, showToast } = useToast();
  const qc = useQueryClient();
  const [persisted] = useState(loadPersisted);

  const [view, setView] = useState<View>(persisted.view);
  const [range, setRange] = useState<AuditRange>(persisted.range);
  const [userId, setUserId] = useState('');
  const [search, setSearch] = useState('');
  const [resultFilter, setResultFilter] = useState<LoginResult[]>([]);
  const [typeFilter, setTypeFilter] = useState<ActivityType[]>([]);
  const [page, setPage] = useState(1);
  const [selectedLogin, setSelectedLogin] = useState<LoginRow | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

  useEffect(() => { try { sessionStorage.setItem('auditlog_view', view); } catch { /* ignore */ } }, [view]);
  useEffect(() => { try { sessionStorage.setItem('auditlog_range', JSON.stringify(range)); } catch { /* ignore */ } }, [range]);

  // Filter changes reset the login table to page 1 in the SAME state update as the filter itself
  // (not a follow-up effect), so no request ever goes out with new filters + a stale page. Switching
  // views resets nothing. The activity list needs no reset: its filters are in the infinite-query
  // key, so a new filter set starts again from the first page on its own.
  const changeRange = (r: AuditRange) => { setRange(r); setPage(1); };
  const changeUserId = (v: string) => { setUserId(v); setPage(1); };
  const changeSearch = (v: string) => { setSearch(v); setPage(1); };
  const changeResultFilter = (v: LoginResult[]) => { setResultFilter(v); setPage(1); };

  const { data: usersRes } = useQuery({
    queryKey: ['audit-users-picker'],
    queryFn: async ({ signal }) => {
      const r = await apiClient.get('/users', { signal });
      return r.data as { success: boolean; data: { id: number; username: string; first_name: string | null; last_name: string | null }[] };
    },
  });
  const userOptions = (usersRes?.data ?? []).map((u) => ({
    value: String(u.id),
    label: [u.first_name, u.last_name].filter(Boolean).join(' ') || u.username,
  }));

  const loginsQuery = useQuery({
    queryKey: ['audit-logins', range, userId, search, resultFilter, page],
    queryFn: async ({ signal }) => {
      const r = await apiClient.get('/audit/logins', {
        signal,
        params: {
          from: range.from, to: range.to, userId: userId || undefined, q: search || undefined,
          result: resultFilter.length ? resultFilter.join(',') : undefined,
          page, pageSize: PAGE_SIZE,
        },
      });
      return (r.data.data as LoginsResponse);
    },
    enabled: view === 'logins',
    staleTime: AUDIT_STALE_MS,
    // Page/filter changes keep showing the previous rows + KPIs until the new page lands, instead of
    // flashing an empty table.
    placeholderData: keepPreviousData,
  });

  // Activity log -- infinite query keyed on the filters; rows are DERIVED from the cached pages,
  // never copied into local state. (The old version mirrored query data into state via an effect
  // keyed on activityQuery.data and cleared it on every view switch; switching back returned the
  // same cached data reference, the effect never re-ran, and the list stayed empty.)
  const activityQuery = useInfiniteQuery({
    queryKey: ['audit-activity', range, userId, search, typeFilter],
    initialPageParam: null as number | null,
    queryFn: async ({ signal, pageParam }) => {
      const r = await apiClient.get('/audit/activity', {
        signal,
        params: {
          from: range.from, to: range.to, userId: userId || undefined, q: search || undefined,
          types: typeFilter.length ? typeFilter.join(',') : undefined,
          cursor: pageParam ?? undefined, limit: PAGE_SIZE,
        },
      });
      return (r.data.data as ActivityResponse);
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: view === 'activity',
    staleTime: AUDIT_STALE_MS,
    placeholderData: keepPreviousData,
  });
  const activityPages = activityQuery.data?.pages;
  const activityRows = useMemo<ActivityRow[]>(() => (activityPages ?? []).flatMap((p) => p.rows), [activityPages]);
  const activityFirstPage = activityPages?.[0];
  const activityLastPage = activityPages?.[activityPages.length - 1];

  // Selected login: keep the id, read the row from the current page when it's there (so the panel
  // reflects refetched data), falling back to the clicked snapshot when it's paged/filtered out.
  const selectedLoginRow = selectedLogin
    ? (loginsQuery.data?.rows.find((r) => r.id === selectedLogin.id) ?? selectedLogin)
    : null;

  async function exportCsv() {
    try {
      const params: Record<string, string> = { view, from: range.from, to: range.to };
      if (userId) params.userId = userId;
      if (search) params.q = search;
      if (view === 'logins' && resultFilter.length) params.result = resultFilter.join(',');
      if (view === 'activity' && typeFilter.length) params.types = typeFilter.join(',');
      const res = await apiClient.get('/audit/export.csv', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-${view}-${range.to}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      showToast('Export failed', true);
    }
  }

  function toggleExpand(id: number) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  // View switch sliding highlight -- same JS-measured technique as Settings > Development's inner
  // tab bar (DevelopmentTab): left/width of the ACTIVE button in px. Re-measured on view change and
  // on window resize (label widths can shift with font loading / zoom).
  const viewBtnRefs = useRef<Record<View, HTMLButtonElement | null>>({ logins: null, activity: null });
  const [viewHighlight, setViewHighlight] = useState({ left: 0, width: 0 });
  useLayoutEffect(() => {
    const measure = () => {
      const el = viewBtnRefs.current[view];
      if (el) setViewHighlight({ left: el.offsetLeft, width: el.offsetWidth });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [view]);

  return (
    <div>
      <Toast toast={toast} />
      {/* Header row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontSize: '1.375rem', fontWeight: 600, letterSpacing: '-0.01em' }}>Audit log</h2>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>
            Every sign-in attempt and every change made in ProQure, across all users. Read-only.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {/* View switch -- literal copy of the Settings > Development inner tab bar structure:
              shared pill-tab-container / pill-tab-slide-highlight / pill-tab-slide-btn classes,
              highlight positioned only by measured width + translateX(px). */}
          <div role="tablist" className="pill-tab-container" style={{ position: 'relative', overflow: 'hidden' }}>
            <div className="pill-tab-slide-highlight" style={{ width: viewHighlight.width, transform: `translateX(${viewHighlight.left}px)` }} />
            <button
              ref={(el) => { viewBtnRefs.current.logins = el; }}
              role="tab"
              aria-selected={view === 'logins'}
              className={`order-tab pill-tab pill-tab-slide-btn${view === 'logins' ? ' active' : ''}`}
              style={{ position: 'relative', zIndex: 1 }}
              onClick={() => setView('logins')}
            >
              <LogIn size={14} className="btn-icon order-tab-icon" /> Login activity
            </button>
            <button
              ref={(el) => { viewBtnRefs.current.activity = el; }}
              role="tab"
              aria-selected={view === 'activity'}
              className={`order-tab pill-tab pill-tab-slide-btn${view === 'activity' ? ' active' : ''}`}
              style={{ position: 'relative', zIndex: 1 }}
              onClick={() => setView('activity')}
            >
              <Activity size={14} className="btn-icon order-tab-icon" /> Activity log
            </button>
          </div>
          <button className="audit-export-btn" onClick={exportCsv}>
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <AuditFilterBar
          view={view}
          range={range}
          onRangeChange={changeRange}
          userId={userId}
          onUserIdChange={changeUserId}
          userOptions={userOptions}
          search={search}
          onSearchChange={changeSearch}
          resultFilter={resultFilter}
          onResultFilterChange={changeResultFilter}
          typeFilter={typeFilter}
          onTypeFilterChange={setTypeFilter}
          resultCounts={loginsQuery.data?.resultCounts ?? {}}
          typeCounts={activityFirstPage?.typeCounts ?? {}}
        />
      </div>

      {view === 'logins' && loginsQuery.data && (
        <div className="audit-in" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 16 }}>
          <AuditKpiCard
            label="Successful sign-ins" Icon={Check} color="#10b981"
            value={loginsQuery.data.kpis.successes24h}
            sub={`last 24 h · ${loginsQuery.data.kpis.successUsers24h} users`}
          />
          <AuditKpiCard
            label="Failed attempts" Icon={XIcon} color="#ef4444"
            value={loginsQuery.data.kpis.failed24h}
            sub={loginsQuery.data.kpis.failedTopIpCount > 0 ? `${loginsQuery.data.kpis.failedTopIpCount} from one IP${loginsQuery.data.kpis.failedTopIpBlocked ? ' · blocked' : ''}` : 'last 24 h'}
            subColor={loginsQuery.data.kpis.failedTopIpCount > 0 ? '#ef4444' : undefined}
          />
          <AuditKpiCard
            label="Unique IPs" Icon={Globe} color="#3b82f6"
            value={loginsQuery.data.kpis.uniqueIps24h}
            sub={loginsQuery.data.kpis.newIps24h > 0 || loginsQuery.data.kpis.flaggedIps24h > 0 ? `${loginsQuery.data.kpis.newIps24h} new · ${loginsQuery.data.kpis.flaggedIps24h} flagged` : `${loginsQuery.data.kpis.newIps24h} new · ${loginsQuery.data.kpis.flaggedIps24h} flagged`}
            subColor={(loginsQuery.data.kpis.newIps24h > 0 || loginsQuery.data.kpis.flaggedIps24h > 0) ? '#f59e0b' : undefined}
          />
          <AuditKpiCard
            label="Active sessions" Icon={Monitor} color="#8b5cf6"
            value={loginsQuery.data.kpis.activeSessions}
            sub={loginsQuery.data.kpis.lockedAccounts > 0 ? `${loginsQuery.data.kpis.lockedAccounts} account(s) locked` : 'none locked'}
            subColor={loginsQuery.data.kpis.lockedAccounts > 0 ? '#f59e0b' : undefined}
          />
        </div>
      )}

      {view === 'logins' ? (
        <LoginActivityTable
          rows={loginsQuery.data?.rows ?? []}
          loading={loginsQuery.isLoading}
          total={loginsQuery.data?.total ?? 0}
          page={page}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
          onRowClick={setSelectedLogin}
          selectedId={selectedLogin?.id ?? null}
        />
      ) : (
        <ActivityTimeline
          rows={activityRows}
          loading={activityQuery.isLoading && activityRows.length === 0}
          total={activityFirstPage?.total ?? 0}
          nextCursor={activityLastPage?.nextCursor ?? null}
          onLoadMore={() => { if (!activityQuery.isFetchingNextPage) activityQuery.fetchNextPage(); }}
          expandedIds={expandedIds}
          onToggleExpand={toggleExpand}
        />
      )}

      {/* key: remount per row so the panel's own confirm/busy state never carries over to the next
          login opened (e.g. block-confirm left open, closed with Esc, then another row clicked). */}
      <LoginDetailPanel
        key={selectedLogin?.id ?? 'none'}
        row={selectedLoginRow}
        onClose={() => setSelectedLogin(null)}
        onChanged={() => qc.invalidateQueries({ queryKey: ['audit-logins'] })}
        showToast={showToast}
      />
    </div>
  );
}
